import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useHeader } from '@app/components/header/layouts/HeaderContext';
import { BaseBreadcrumbWithRender } from '@app/components/common/BaseBreadcrumb/BaseBreadcrumb';
import { PageTitle } from '@app/components/common/PageTitle/PageTitle';
import ReportFilterBar from './ReportFilterBar';
import { message, Space } from 'antd';
import { DownloadOutlined, FileExcelOutlined, EditOutlined } from '@ant-design/icons';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { DetailedEstRegsReport } from '@app/components/apps/Reports/PRReports/PRDetailedEstReportLayout';

const breadcrumbMap: Record<string, { name: string; path: string }> = {
  lab: { name: 'Lab Reports', path: '/lab-reports' },
  'pr&license': { name: 'PR & License Reports', path: '/pr-license-reports' },
  'import&export': { name: 'Import & Export Reports', path: '/import-export-reports' },
  finance: { name: 'Financial Reports', path: '/finance-reports' },
  followup: { name: 'Follow-up Reports', path: '/followup-reports' },
};

// Title Mapping
const titleMap: Record<string, string> = {
  'earnings-by-divisions': 'Lab - Earnings by Divisions',
  'test-count-by-divisions': 'Lab - Test Count by Divisions',
  'sample-count-by-divisions': 'Lab - Sample Count by Divisions',
  'item-qty-by-divisions': 'Lab - Item Quantity by Divisions',
  'earnings-by-divisions-nom-pay': 'Lab - Earnings by Divisions For Nominal Payments',
  'test-count-by-divisions-nom-pay': 'Test Count by Divisions For Nominal Payments',
  'pr-earnings-by-departments': 'PR & License - Earnings by Departments',
  'reg-count-by-departments': 'PR & License - Registration Count by Departments',
  'registrations-by-establishment': 'Registrations by Establishment',
};

//Layout Type Mapping
type ReportLayout = 'lab-expandable' | 'lab-flat' | 'pr-license' | 'pr-est';

const reportLayoutMap: Record<string, ReportLayout> = {
  // Lab - Expandable
  'earnings-by-divisions': 'lab-expandable',
  'test-count-by-divisions': 'lab-expandable',
  'earnings-by-divisions-nom-pay': 'lab-expandable',
  'test-count-by-divisions-nom-pay': 'lab-expandable',

  // Lab - Flat
  'sample-count-by-divisions': 'lab-flat',
  'item-qty-by-divisions': 'lab-flat',

  // PR & License
  'pr-earnings-by-departments': 'pr-license',
  'pr-count-by-departments': 'pr-license',
  'registrations-by-establishment': 'pr-est',
};

const AdditionalReportPage: React.FC = () => {
  const location = useLocation();
  const { setBreadcrumb } = useHeader();

  const reportKey = location.state?.reportKey || 'registrations-by-establishment';
  const repType = location.state?.repType || (localStorage.getItem('selected_rep_type') as string) || 'lab';
  const initialFilters = location.state?.filters || {};

  const [currentBreadcrumbName, setCurrentBreadcrumbName] = useState<string>(titleMap[reportKey || ''] || 'Report');
  const [currentFilters, setCurrentFilters] = useState(initialFilters);
  const [isEditMode, setIsEditMode] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);
  const locationName = location.state?.location_name || 'Establishment';
  const locationId = location.state?.id;

  useEffect(() => {
    const parent = breadcrumbMap[repType] || { path: '/', name: 'Reports' };
    const routes = [
      { path: parent.path, breadcrumbName: parent.name },
      {
        path: '/report',
        breadcrumbName: titleMap[reportKey] || 'Report',
        state: { filters: currentFilters, reportKey, repType },
      },
      { path: `/report/detailed`, breadcrumbName: `All registrations of "${locationName}"` },
    ];

    setBreadcrumb(<BaseBreadcrumbWithRender routes={routes} separator=">" />);
    return () => setBreadcrumb(undefined);
  }, [repType, reportKey, currentFilters, locationName, setBreadcrumb]);

  const handleFiltersChange = (newFilters: any) => {
    setCurrentFilters(newFilters);
  };

  const addPDFStyles = () => {
    const style = document.createElement('style');
    style.id = 'pdf-styles';
    style.innerHTML = `
    /* Hide elements in PDF */
    .hide-in-pdf, .ant-table-row-expand-icon {
      display: none !important;
    }
  `;
    document.head.appendChild(style);
  };

  const removePDFStyles = () => {
    const style = document.getElementById('pdf-styles');
    if (style) style.remove();
  };

  const handleDownloadPDF = async () => {
    if (!reportRef.current) return;

    addPDFStyles();

    // Hide elements that should not appear in PDF
    const elementsToHide = reportRef.current.querySelectorAll('.hide-in-pdf');
    elementsToHide.forEach((el) => ((el as HTMLElement).style.display = 'none'));

    try {
      // Capture the component
      const canvas = await html2canvas(reportRef.current, {
        scale: 2, // higher resolution
        useCORS: true,
        logging: false,
      });

      const imgData = canvas.toDataURL('image/png');

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 8; // page margin in mm

      // Canvas dimensions in px
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;

      // PDF dimensions in px (using 1mm ~ 3.78px)
      const pdfPageHeightPx = ((pageHeight - margin * 2) * canvasWidth) / (pageWidth - margin * 2);

      let remainingHeight = canvasHeight;
      let position = 0;

      while (remainingHeight > 0) {
        const canvasChunk = document.createElement('canvas');
        canvasChunk.width = canvas.width;
        canvasChunk.height = Math.min(pdfPageHeightPx, remainingHeight);

        const ctx = canvasChunk.getContext('2d');
        if (ctx) {
          ctx.drawImage(canvas, 0, position, canvas.width, canvasChunk.height, 0, 0, canvas.width, canvasChunk.height);
        }

        const imgChunkData = canvasChunk.toDataURL('image/png');
        const pdfHeight = (canvasChunk.height * (pageWidth - margin * 2)) / canvas.width;

        if (position > 0) pdf.addPage();
        pdf.addImage(imgChunkData, 'PNG', margin, margin, pageWidth - margin * 2, pdfHeight);

        remainingHeight -= canvasChunk.height;
        position += canvasChunk.height;
      }

      pdf.save(`${currentBreadcrumbName}.pdf`);
    } catch (err) {
      console.error(err);
      message.error('Failed to generate PDF.');
    } finally {
      // Restore hidden elements
      elementsToHide.forEach((el) => ((el as HTMLElement).style.display = ''));
      removePDFStyles();
    }
  };

  const handleConvertToCSV = () => {
    message.info('CSV conversion functionality to be implemented');
  };

  const handleEditFilters = () => {
    setIsEditMode(true);
  };

  if (!locationId) return <div>No establishment selected.</div>;

  const layoutType = reportLayoutMap[reportKey];

  return (
    <div>
      <PageTitle>{currentBreadcrumbName}</PageTitle>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Space>
          {!isEditMode && (
            <>
              <BaseButton icon={<DownloadOutlined />} onClick={handleDownloadPDF} type="primary" size="small">
                Download as PDF
              </BaseButton>
              <BaseButton icon={<FileExcelOutlined />} onClick={handleConvertToCSV} type="primary" size="small">
                Convert to CSV
              </BaseButton>
            </>
          )}
        </Space>
      </div>
      <div>
        <ReportFilterBar
          reportKey={reportKey}
          initialFilters={currentFilters}
          onFiltersChange={handleFiltersChange}
          isEditMode={false}
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onEditModeChange={() => {}}
        />
        <br />

        <DetailedEstRegsReport location_id={locationId} location_name={locationName} filters={currentFilters} />
      </div>
    </div>
  );
};

export default AdditionalReportPage;
